import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate } from '../../../utils/datetime';
import { MethodDefaults } from './MethodDefaults';
import { PickListDialog } from './PickListDialog';
import { TestParamDefaults, type RowEdit } from './TestParamDefaults';
import { buildSave, cellEdit, type GridRow, type Option, type ParamRow } from './testParamTabs';
import './testMethodDetail.css';

/** GET /api/v1/test-methods/{id} — G5's General Information fields. */
interface TestMethodView {
  testCode: string | null; testName: string | null; matrixName: string | null; methodName: string | null;
  category: string | null; isGroup: boolean; departmentName: string | null; comment: string | null; retireDate: string | null;
}

const FORM = 'Test Code';
const errText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

/**
 * The test's own window, "{Test} - Test" — G5 TestMethod_DetailView as it opens from the
 * Contract Lab Test Pricing "Tests" picker. Rename Test · General Information · Group Test
 * (group tests only, as G5 hides the rest for them) or Method Defaults + Test Parameter
 * Defaults. Footer OK stores the parameter cells edited and the rows ADDed in the six
 * parameter tabs (one transaction); Cancel discards them. Every other button changes the
 * data at once, as in G5. Each tab is filtered to THIS test — G5 opened from that picker
 * loses its filter and lists all 5,169 parameters of every test, which is not copied.
 */
export function TestMethodDetailDialog({ testMethodId, onClose, overlayClassName }: { testMethodId: string; onClose: () => void; overlayClassName?: string }) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const base = `/api/v1/test-methods/${testMethodId}`;
  const [view, setView] = useState<TestMethodView | null>(null);
  const [rows, setRows] = useState<GridRow[]>([]);
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [units, setUnits] = useState<Option[]>([]);
  const [qcTypes, setQcTypes] = useState<Option[]>([]);
  const [renaming, setRenaming] = useState(false);

  const dirty = rows.some((r) => r.pending) || Object.keys(edits).length > 0;
  useUnsavedGuard(dirty);

  const loadView = useCallback(async () => {
    try { setView((await api.get<{ data?: TestMethodView }>(base)).data.data ?? null); }
    catch { notify.error('The test could not be loaded.'); }
  }, [base, notify]);

  const loadQcTypes = useCallback(async () => {
    try {
      const res = await api.get<{ data?: { id: string; qcType: string | null }[] }>(`${base}/qc-types`);
      setQcTypes((res.data?.data ?? []).map((q) => ({ id: q.id, name: q.qcType })));
    } catch { /* the QC Types tab reports its own load failure */ }
  }, [base]);

  /** Re-reads the saved rows; ADDed rows and buffered edits of rows still there are kept. */
  const loadParams = useCallback(async () => {
    try {
      const res = await api.get<{ data?: ParamRow[] }>(`${base}/parameters`);
      const fresh = (res.data?.data ?? []).map((r) => ({ ...r, key: r.oid }) as GridRow);
      const live = new Set(fresh.map((r) => r.key));
      setRows((cur) => [...cur.filter((r) => r.pending), ...fresh]);
      setEdits((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => k.startsWith('new-') || live.has(k))));
    } catch { notify.error('The test parameters could not be loaded.'); }
  }, [base, notify]);

  useEffect(() => {
    void loadView();
    void loadParams();
    void loadQcTypes();
    api.get<{ data?: Option[] }>('/api/v1/test-methods/units').then(({ data }) => setUnits(data.data ?? [])).catch(() => undefined);
  }, [loadView, loadParams, loadQcTypes]);

  const isRows = useMemo(() => rows.filter((r) => r.internalStandard && !r.pending), [rows]);

  const ok = () => {
    if (!dirty) { onClose(); return; }
    const body = buildSave(rows, Object.fromEntries(Object.entries(edits).map(([k, e]) => [k, e.wire])));
    void runSave({
      formName: FORM,
      auditAction: 'edit',
      save: async () => { await api.post(`${base}/parameters/save`, body); },
      onSaved: () => { setEdits({}); setRows([]); onClose(); },
      onError: (e) => notify.error(errText(e, 'The test parameters could not be saved.')),
    });
  };
  const cancel = () => { void confirmDiscardIfUnsaved().then((leave) => { if (leave) onClose(); }); };

  const field = (label: string, value: string | null | undefined) => (
    <>
      <span className="tmv-label">{label}:</span>
      <span className="tmv-value">{value || ' '}</span>
    </>
  );

  return (
    <AppDialog
      open
      title={`${view?.testName ?? 'Test'} - Test`}
      width={1100}
      className={`dlg-center${overlayClassName ? ` ${overlayClassName}` : ''}`}
      maximizable
      onClose={cancel}
      submitOnEnter={false}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={ok}>OK</button>
          <button type="button" className="ca-btn" onClick={cancel}>Cancel</button>
        </>
      }
    >
      <div className="tmd-bar">
        <button type="button" className="ca-btn" onClick={() => setRenaming(true)} disabled={!view}>
          <span className="tb-i tb-i-blue">✎</span> Rename Test
        </button>
        {dirty && <span className="clp-pending">Unsaved changes — press OK to save them.</span>}
      </div>

      <h3 className="tmv-h">General Information</h3>
      <div className="tmv-grid">
        <div className="tmv-col">
          {field('Matrix', view?.matrixName)}
          {field('Test Name', view?.testName)}
          <span className="tmv-label" />
          <label className="tmv-check">
            <input type="checkbox" checked={!!view?.isGroup} readOnly disabled /> Is Group
          </label>
          {/* G5 hides Method on a group test. */}
          {!view?.isGroup && field('Method', view?.methodName)}
          {field('Test Code', view?.testCode)}
        </div>
        <div className="tmv-col">
          {field('Category', view?.category)}
          {field('Department', view?.departmentName || 'N/A')}
          {field('Comment', view?.comment)}
          {field('Retire Date', view?.retireDate ? fmtLocalDate(view.retireDate) : null)}
        </div>
      </div>

      {view?.isGroup ? (
        <GroupTests base={base} />
      ) : view ? (
        <>
          <MethodDefaults testId={testMethodId} onQcTypesChanged={() => { void loadQcTypes(); }} />
          <TestParamDefaults
            testId={testMethodId}
            rows={rows}
            edits={edits}
            units={units}
            qcTypes={qcTypes}
            onCellEdit={(row, col, value) => {
              const e = cellEdit(col, value, units, isRows);
              setEdits((cur) => {
                const prev = cur[row.key] ?? { wire: {}, display: {} };
                return { ...cur, [row.key]: { wire: { ...prev.wire, ...e.wire }, display: { ...prev.display, ...e.display } } };
              });
            }}
            onAdd={(added) => setRows((cur) => [...added, ...cur])}
            onDropPending={(keys) => {
              setRows((cur) => cur.filter((r) => !keys.has(r.key)));
              setEdits((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !keys.has(k))));
            }}
            onReload={loadParams}
          />
        </>
      ) : null}

      {renaming && view && (
        <RenameTestDialog base={base} current={view.testName ?? ''} onClose={() => setRenaming(false)}
          onRenamed={() => { setRenaming(false); void loadView(); }} />
      )}
    </AppDialog>
  );
}

/** G5 Rename Test (TestMethod_DetailView_RenameTest): one field, accept button "Rename". */
function RenameTestDialog({ base, current, onClose, onRenamed }: { base: string; current: string; onClose: () => void; onRenamed: () => void }) {
  const notify = useNotify();
  const [name, setName] = useState(current);
  const [saving, setSaving] = useState(false);
  const rename = async () => {
    if (!name.trim()) { notify.warning('Test name cannot be empty.'); return; }
    setSaving(true);
    try {
      await api.put(`${base}/rename`, { testName: name.trim() });
      notify.success('Test renamed successfully.');
      onRenamed();
    } catch (e) { notify.error(errText(e, 'The test could not be renamed.')); }
    finally { setSaving(false); }
  };
  return (
    <AppDialog open title="Rename Test" width={400} className="dlg-center" onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => { void rename(); }}>Rename</button>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      <div className="tmd-form">
        <label>Test Name</label>
        <input className="sd-input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
      </div>
    </AppDialog>
  );
}

interface GroupRow extends Record<string, unknown> { oid: string; test: string | null; method: string | null; component: string | null; parameter: string | null }
const GROUP_COLUMNS: AppColumn<GroupRow>[] = [
  { field: 'test', header: 'Test', width: 200 }, { field: 'method', header: 'Method', width: 150 },
  { field: 'component', header: 'Component', width: 130 }, { field: 'parameter', header: 'Parameter' },
];

/** G5 "Group Test" (GroupTestMethod_ListView_IsGroup): Add picks other tests' parameters; Remove. Immediate. */
function GroupTests({ base }: { base: string }) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [rows, setRows] = useState<GroupRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get<{ data?: GroupRow[] }>(`${base}/group-tests`)).data.data ?? []); setSelected(new Set()); }
    catch { notify.error('The group tests could not be loaded.'); }
    finally { setLoading(false); }
  }, [base, notify]);
  useEffect(() => { void load(); }, [load]);

  const remove = async () => {
    if (selected.size === 0) { notify.info('Please select an item to proceed further.'); return; }
    if (!(await confirm({ title: 'Remove', tone: 'danger', confirmLabel: 'Yes',
      message: 'You are about to remove selected record(s). Do you want to proceed?' }))) return;
    try { await api.post(`${base}/group-tests/remove`, { ids: [...selected] }); notify.success('Removed successfully.'); }
    catch (e) { notify.error(errText(e, 'The tests could not be removed.')); }
    await load();
  };

  return (
    <div className="tmd-section">
      <h3 className="tmv-h">Group Test</h3>
      <div className="tmv-panel">
        <AppToolbar noHotkeys showMeta={false} crudFirst newLabel="Add" onNew={() => setPicking(true)}
          deleteLabel="Remove" onDelete={() => { void remove(); }}
          exportData={{ filename: 'Group Test', ...gridExportMatrix(rows, GROUP_COLUMNS) }} />
        <AppDataGrid<GroupRow> rows={rows} columns={GROUP_COLUMNS} getRowId={(r) => r.oid} loading={loading}
          selectable selectedIds={selected} onSelectionChange={setSelected} defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
      </div>
      {picking && (
        <PickListDialog<GroupRow> title="Tests" url={`${base}/group-tests/available`} columns={GROUP_COLUMNS} getId={(r) => r.oid} width={900}
          onClose={() => setPicking(false)}
          onPicked={(ps) => {
            setPicking(false);
            void api.post(`${base}/group-tests/add`, { ids: ps.map((p) => p.oid) })
              .then(() => notify.success('Added successfully.'))
              .catch((e) => notify.error(errText(e, 'The tests could not be added.')))
              .finally(() => { void load(); });
          }} />
      )}
    </div>
  );
}
